import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { pickBy } from 'lodash';
import useValidationSchema from './_form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IPatientSearchForm, IPatientSearchFormValues, IPatientSearchCriteria } from './_types';
import PatientSearchFormInner from './PatientSearchFormInner';
import { format } from 'date-fns';
import { useStorage } from 'utils/hooks/useStorage';

export const PatientSearchForm: React.FC<IPatientSearchForm> = ({ onSubmit }) => {
  const { t } = useTranslation('Patients');
  const [searchFieldOpened, setSearchFieldOpened] = useState(false);
  const { PatientSearchFormSchema } = useValidationSchema(t);
  const { getItem, putItem } = useStorage();

  const methods = useForm<IPatientSearchFormValues>({
    resolver: yupResolver(PatientSearchFormSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      birthNumber: '',
      firstName: '',
      lastName: '',
      birthDateFrom: null,
      birthDateTo: null,
      city: '',
      street: '',
      postalCode: '',
      state: '',
      gender: '',
      deceased: false,
      deceasedDateFrom: null,
      deceasedDateTo: null,
      phone: '',
      email: '',
    },
  });

  const { handleSubmit, watch, reset } = methods;

  useEffect(() => {
    const savedValues = getItem('patientSearchFormValues');
    if (savedValues) {
      try {
        const parsedValues = JSON.parse(savedValues);
        // Convert date strings back to Date objects
        if (parsedValues.birthDateFrom)
          parsedValues.birthDateFrom = new Date(parsedValues.birthDateFrom);
        if (parsedValues.birthDateTo) parsedValues.birthDateTo = new Date(parsedValues.birthDateTo);
        if (parsedValues.deceasedDateFrom)
          parsedValues.deceasedDateFrom = new Date(parsedValues.deceasedDateFrom);
        if (parsedValues.deceasedDateTo)
          parsedValues.deceasedDateTo = new Date(parsedValues.deceasedDateTo);
        reset(parsedValues);
      } catch (e) {
        console.error('Failed to parse saved patient search values', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allFormValues = watch();

  useEffect(() => {
    putItem('patientSearchFormValues', JSON.stringify(allFormValues));
  }, [allFormValues, putItem]);

  const checkValuesToAllowSubmit = () => {
    const values = Object.values(allFormValues);
    // At least one field must be filled
    return values.some((value) => {
      if (typeof value === 'boolean') return false; // Don't count boolean false as filled
      if (value === null || value === undefined || value === '') return false;
      return true;
    });
  };

  const canSearch = checkValuesToAllowSubmit();

  const transformDatesToString = (date: Date | null): string | undefined => {
    if (!date) return undefined;
    try {
      return format(date, 'yyyy-MM-dd');
    } catch {
      return undefined;
    }
  };

  const handleFormSubmit = (values: IPatientSearchFormValues) => {
    const searchCriteria: IPatientSearchCriteria = {
      birthNumber: values.birthNumber || undefined,
      firstName: values.firstName || undefined,
      lastName: values.lastName || undefined,
      birthDateFrom: transformDatesToString(values.birthDateFrom),
      birthDateTo: transformDatesToString(values.birthDateTo),
      city: values.city || undefined,
      street: values.street || undefined,
      postalCode: values.postalCode || undefined,
      state: values.state || undefined,
      gender: values.gender || undefined,
      deceased: values.deceased || undefined,
      deceasedDateFrom: transformDatesToString(values.deceasedDateFrom),
      deceasedDateTo: transformDatesToString(values.deceasedDateTo),
      phone: values.phone || undefined,
      email: values.email || undefined,
    };

    // Remove undefined values
    const cleanedCriteria = pickBy(
      searchCriteria,
      (value) => value !== undefined && value !== '',
    ) as IPatientSearchCriteria;

    onSubmit(cleanedCriteria);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <PatientSearchFormInner
          searchFieldOpened={searchFieldOpened}
          setSearchFieldOpened={setSearchFieldOpened}
          canSearch={canSearch}
        />
      </form>
    </FormProvider>
  );
};

export default PatientSearchForm;
